import * as React from "react";
import { MobXProviderContext, observer } from "mobx-react";
import { Box, Card, CardMedia, Grid, lighten, Button } from "@material-ui/core";
import { useTheme, ThemeProvider, makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import { MUIChip, MUIProgressBar, themeVariables } from "../";
import * as styles from "./styles.scss";
import { SvgIcon } from "../../../../components";
import { beautyNumbers, tl } from "../../../../utils";

import { CourseCardHours } from "./hours";
import { CourseCardLessons } from "./lessons";
import { CourseCardName } from "./coursename";

interface IProps {
  course: {
    name: string;
    status: string;
    icon?: string;
    cover?: string;
    //open course
    current?: string; //lesson
    points?: number;
    progress?: number;
    deadline?: number;
    //shop/subscribe
    authorLabel?: { mark: string; color: number };
    lessonsAll?: number;
    lessonsTasks?: number;
    lessonsTests?: number;
    hoursTheory?: number | string;
    hoursPractice?: number | string;
  };
  group: { coursesAvailableToUnblock: number; coursesUnblocked: number };
  courseHref?: string;
  onSaleDetailsClick: any;
  onsale?: boolean;
  isStudentCourses?: boolean;
}

export const CourseCardStudent = observer((props: IProps) => {
  const theme = useTheme();

  const { course, courseHref, group, onSaleDetailsClick } = props;

  const isLocked =
    course.status === "subscribe_locked" || course.status === "for-sale";
  const canUnblock =
    course.status === "subscribe_locked" &&
    group.coursesAvailableToUnblock !== group.coursesUnblocked;

  const langStatuses: any = {
    inqueue: tl("Новое"),
    rejected: tl("Незачет"),
    oncheck: tl("На проверке"),
  };

  const lock = (
    <>
      <Box className={styles.lockWrap}>
        <Box
          className={`${styles.lockHandle} ${styles.lockCentered} ${styles.animate}`}
        >
          <SvgIcon type="courses-lock-handle" />
        </Box>
        <Box
          className={`${styles.lockBody} ${styles.lockCentered} ${styles.animate}`}
        >
          <SvgIcon type="courses-lock-body" />
        </Box>
      </Box>
      <Box className={`${styles.buttonDetails} ${styles.animate}`}>
        <Button
          variant="contained"
          fullWidth
          className={`MuiButton-shop`}
          onClick={(e: any) => {
            e.stopPropagation();
            onSaleDetailsClick();
          }}
          tabIndex={-1}
        >
          <Box whiteSpace="nowrap">{tl("Подробнее о курсе")}</Box>
        </Button>
      </Box>
    </>
  );

  const stylesBadges: any = {
    inqueue: {
      backgroundColor: themeVariables.palette.info.light,
      color: themeVariables.palette.info.contrastText,
    },
    rejected: {
      backgroundColor: themeVariables.palette.error.light,
      color: themeVariables.palette.error.contrastText,
    },
  };

  const classes = makeStyles({
    cardCoverOverlayBg: {
      backgroundColor: themeVariables.palette.grey[50],
      opacity: 0.5,
      ["." + styles.cardCover + ":hover &"]: {
        backgroundColor: themeVariables.palette.primary.main,
        opacity: 0.2,
      },
    },
    locked: {
      ["." + styles.cardCover + ":hover &"]: {
        backgroundColor: themeVariables.palette.grey[50],
        opacity: 0.5,
      },
    },
  })({});

  const cardBody = (
    <Box
      clone
      border={themeVariables.border}
      borderRadius={themeVariables.shape.borderRadius}
    >
      <CardMedia
        image={course.icon ? `/media/400/${window.btoa(course.icon)}` : null}
        className={styles.cardCover}
      >
        {!course.icon ? <SvgIcon type="courses-big" /> : null}

        {isLocked ? (
          <Box
            className={`${styles.cardCoverOverlay} ${styles.full}`}
            color={themeVariables.palette.common.white}
          >
            <Box
              className={`${styles.cardCoverOverlayBg} ${
                classes.cardCoverOverlayBg
              } ${canUnblock || !props.isStudentCourses ? "" : classes.locked}`}
            />
            <Box position="relative">
              {course.authorLabel &&
              (course.authorLabel.color || course.authorLabel.color === 0) &&
              course.authorLabel.mark ? (
                <Grid container alignItems="flex-start" justifyContent="flex-end">
                  <Box clone p={1}>
                    <Grid item>
                      <MUIChip
                        size="small"
                        label={course.authorLabel.mark}
                        color={course.authorLabel.color}
                      />
                    </Grid>
                  </Box>
                </Grid>
              ) : null}
            </Box>
            {lock}
          </Box>
        ) : ["blocked", "suspended", "done", "overdue"].includes(
            course.status
          ) ? (
          <Box className={`${styles.cardCoverStatus} ${course.status}`} />
        ) : (
          <Box
            px={1.5}
            py={0.5}
            className={styles.cardCoverOverlay}
            bgcolor={themeVariables.palette.controls}
            color={themeVariables.palette.common.white}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                {course.current ? `${tl("Урок")} ${course.current}` : null}
              </Grid>
              {course.status ? (
                <Grid item>
                  <Box
                    px={1.5}
                    py={0.5}
                    borderRadius={theme.shape.borderRadius}
                    bgcolor={themeVariables.palette.controls}
                    style={stylesBadges[course.status]}
                  >
                    {langStatuses[course.status]}
                  </Box>
                </Grid>
              ) : null}
            </Grid>
          </Box>
        )}
      </CardMedia>
    </Box>
  );

  const hours =
    ((course.hoursTheory || course.hoursTheory === 0) &&
      (course.hoursPractice || course.hoursPractice === 0)) ||
    props.onsale ? (
      <CourseCardHours
        theory={course.hoursTheory}
        practice={course.hoursPractice}
      />
    ) : null;
  const lessons =
    ((course.lessonsTasks || course.lessonsTasks === 0) &&
      (course.lessonsTests || course.lessonsTests === 0)) ||
    props.onsale ? (
      <CourseCardLessons
        total={course.lessonsAll}
        tasks={course.lessonsTasks || 0}
        tests={course.lessonsTests || 0}
      />
    ) : null;
  const progress =
    course.points !== null ||
    course.progress !== null ||
    course.deadline !== null ? (
      <Box p={1.5} pb={0}>
        <Box minHeight={28} clone>
          <Grid container spacing={1} alignItems="center">
            {course.points && course.points !== null ? (
              <Grid item className={styles.cardPoints}>
                <SvgIcon type="star" />
                {beautyNumbers(course.points)}
              </Grid>
            ) : null}
            {course.progress !== null || course.deadline !== null ? (
              <Grid item xs>
                {course.progress !== null ? (
                  <MUIProgressBar
                    data={{
                      variant: "determinate",
                      value: course.progress,
                    }}
                    type="green"
                    size={course.deadline !== null ? "xs" : "sm"}
                  />
                ) : null}
                {course.deadline !== null ? (
                  <MUIProgressBar
                    data={{
                      variant: "determinate",
                      value: course.deadline,
                    }}
                    type={
                      course.progress !== null &&
                      course.deadline >= course.progress
                        ? "red"
                        : "green"
                    }
                    size={course.progress !== null ? "xs" : "sm"}
                    mt={course.progress !== null ? 0.5 : null}
                  />
                ) : null}
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      clone
      flex="1"
      border={themeVariables.border}
      borderRadius={themeVariables.shape.borderRadius}
    >
      <Card className={`${styles.cardBody} {classes.cardBody}`}>
        {courseHref && !isLocked ? (
          <a href={courseHref}>{cardBody}</a>
        ) : (
          cardBody
        )}
        {props.isStudentCourses ? (
          <>{isLocked ? lessons || hours : progress}</>
        ) : (
          <>
            {lessons}
            {hours}
            {progress}
          </>
        )}
        <CourseCardName name={course.name} href={courseHref} />
      </Card>
    </Box>
  );
});
